import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Copy, MagnifyingGlass } from "@phosphor-icons/react";
import { RepCard } from "../shared/RepCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { api, formatApiErrorDetail } from "../../lib/api";
import { usePolling } from "../../hooks/usePolling";

export function ManagerDownlineTab() {
  const navigate = useNavigate();
  const location = useLocation();
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [search, setSearch] = useState("");
  const visibleReps = reps.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (r.display_name || "").toLowerCase().includes(q) || (r.user_id || "").toLowerCase().includes(q);
  });

  const fetchReps = () => {
    api.get("/manager/reps/cards")
      .then(({ data }) => { setReps(data); setLoadFailed(false); })
      .catch((e) => { setLoadFailed(true); toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load your reps. Please refresh."); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReps();
  }, []);

  // Rep Card Live Sync (Aug 2026): background-only refresh — updates rep call counts/status in
  // place via React state, never reloads or navigates the page, never resets scroll position.
  // (The earlier 15s auto-poll here was removed Sep 18 2026 after a report of kicking the manager
  // back to Overview — that was a different, buggy implementation; usePolling is the same safe
  // background-fetch hook already proven on /admin/rep-activity and /admin/rep-scorecards.)
  usePolling(fetchReps, 15000);

  const viewAs = async (rep) => {
    try {
      await api.post(`/manager/reps/${rep.id}/view-as`);
      window.location.href = "/rep";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const resetPassword = async (rep) => {
    if (!window.confirm(`Reset ${rep.display_name}'s password? They'll be required to set a new one on next login.`)) return;
    try {
      const { data } = await api.post(`/manager/reps/${rep.id}/reset-password`, {});
      setResetResult({ display_name: rep.display_name, ...data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const unlockRep = async (rep) => {
    try {
      const { data } = await api.post(`/manager/reps/${rep.id}/unlock`, {});
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const clearLastLogin = async (rep) => {
    if (!window.confirm(`Clear ${rep.display_name}'s Last Login stat back to "Never"?`)) return;
    try {
      const { data } = await api.post(`/manager/reps/${rep.id}/clear-last-login`, {});
      toast.success(data.message);
      fetchReps();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const clearCallsAttempted = async (rep) => {
    if (!window.confirm(`Clear ${rep.display_name}'s Attempted Calls, Sweep Calls, and Unique Leads Called back to 0? This cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/manager/reps/${rep.id}/clear-calls-attempted`, {});
      toast.success(data.message);
      fetchReps();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const copyResetPassword = () => {
    if (resetResult?.temp_password) {
      navigator.clipboard.writeText(resetResult.temp_password);
      toast.success("Copied");
    }
  };

  // Build 13 fix (Sep 19 2026) — "Jump to Rep" from a manager's own search lands here with
  // ?focus=<id>; scroll that rep's card into view once the list has loaded.
  const [focusedRepId, setFocusedRepId] = useState(null);
  useEffect(() => {
    const focusId = new URLSearchParams(location.search).get("focus");
    if (focusId) setFocusedRepId(focusId);
  }, [location.search]);
  useEffect(() => {
    if (!focusedRepId || reps.length === 0) return;
    const el = document.querySelector(`[data-testid="rep-card-${focusedRepId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    const t = setTimeout(() => setFocusedRepId(null), 4000);
    return () => clearTimeout(t);
  }, [focusedRepId, reps]);

  return (
    <div data-testid="manager-downline-tab">
      <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-1">Your Reps</h2>
      <p className="text-igc-muted text-sm mb-4">Their Activity — call attempts, deals, and commissions at a glance.</p>
      {loadFailed && (
        <div className="text-igc-danger text-sm bg-igc-surface border border-igc-danger/30 p-5 mb-4 flex items-center justify-between" data-testid="manager-downline-error">
          <span>Couldn't load your reps — this is a network/loading error, not an empty team.</span>
          <Button data-testid="manager-downline-retry-button" onClick={fetchReps} size="sm" title="Tries loading your rep list again." className="bg-igc-gold hover:bg-igc-goldHover text-black">Retry</Button>
        </div>
      )}
      {!loading && !loadFailed && reps.length === 0 && (
        <p className="text-igc-muted text-sm bg-igc-surface border border-igc-gold/10 p-5" data-testid="manager-downline-empty">
          Your reps will appear here once onboarded.
        </p>
      )}
      {reps.length > 0 && (
        <div className="relative mb-4 max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-igc-muted" />
          <Input
            data-testid="manager-downline-search-input"
            placeholder="Search by name or rep code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            title="Search narrows the rep cards below in real time as you type — matches name or rep code."
            className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream pl-8"
          />
        </div>
      )}
      {reps.length > 0 && visibleReps.length === 0 && (
        <p className="text-igc-muted text-sm" data-testid="manager-downline-search-empty">No reps match "{search}".</p>
      )}
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="manager-rep-card-grid" data-hscroll>
        {visibleReps.map((r) => (
          <div key={r.id} className={r.id === focusedRepId ? "ring-2 ring-igc-gold rounded transition-all" : ""}>
            <RepCard
              rep={r}
              onOpen={(rep) => navigate(`/manager/reps/${rep.id}`)}
              onViewAs={viewAs}
              onResetPassword={resetPassword}
              onUnlock={unlockRep}
              onClearLastLogin={clearLastLogin}
              onClearCallsAttempted={clearCallsAttempted}
            />
          </div>
        ))}
      </div>
      <Dialog open={!!resetResult} onOpenChange={() => setResetResult(null)}>
        <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="manager-reset-password-result-modal">
          <DialogHeader><DialogTitle className="font-serif">Password Reset</DialogTitle></DialogHeader>
          {resetResult && (
            <div className="space-y-3">
              <p className="text-igc-muted text-sm">Share this securely with {resetResult.display_name} — they'll be required to set a new password on next login.</p>
              <div className="bg-igc-surfaceLight border border-igc-gold/20 p-4 space-y-1" data-testid="manager-reset-password-credentials">
                <p className="text-igc-cream text-sm">Temp Password: <span className="text-igc-goldText" data-testid="manager-reset-password-temp-value">{resetResult.temp_password}</span></p>
              </div>
              <Button data-testid="manager-copy-reset-password-button" onClick={copyResetPassword} title="Copies this temporary password to your clipboard so you can share it with the rep." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black flex items-center gap-2 justify-center">
                <Copy size={16} weight="duotone" /> Copy Temp Password
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
